import { Link as GatsbyLink } from 'gatsby';
import { GatsbyImage, GatsbyImageProps, getSrcSet, IGatsbyImageData } from 'gatsby-plugin-image';
import isAbsoluteURL from 'is-absolute-url';
import React, { useMemo } from 'react';

import { ButtonOwnProps, Chip, Button as MuiButton, Stack } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';

import {
  useContentfulInspectorMode, useContentfulLiveUpdates
} from '../shared/hooks/contentful';
import { initiateCustomerLogin } from '../shared/shopify/auth/customer/initiate';
import { notPrintable } from '../shared/styles/notPrintable';

export interface HomepageLink {
  id: string
  href: string
  url: string
  text: string
  contentful_id: string
}

interface ContentfulAssetImageProps
  extends Omit<Omit<GatsbyImageProps, "alt">, "image"> {
  asset: ContentfulAsset
  alt?: string
  aboveFold?: boolean
  skipGatsbyImage?: boolean
  width?: string | number,
  height?: string | number,
  // sizes?: string,
  contentfulPreviewProps?: any
}

export type ContentfulAsset = {
  id?: string
  title?: string
  url?: string
  contentful_id?: string
  gatsbyImageData?: IGatsbyImageData
  localFile?: {
    childImageSharp: {
      gatsbyImageData: IGatsbyImageData
    }
  }
}

/// Supports SVGs, childImageSharp is null for SVGs
export function ContentfulAssetImage({
  asset,
  loading,
  alt,
  style,
  imgStyle,
  aboveFold,
  width,
  height,
  // sizes,
  contentfulPreviewProps,
  title,
  skipGatsbyImage
}: ContentfulAssetImageProps) {
  const imageData = asset?.localFile?.childImageSharp?.gatsbyImageData || asset?.gatsbyImageData
  // const imageData = asset.localFile ? getImage(asset.localFile) : (asset.gatsbyImageData ? getImage(asset.gatsbyImageData) : undefined)

  // if (skipGatsbyImage) {
  //   console.log('heree', asset, imageData)
  // }

  const dimensions = {}
  if (width) {
    dimensions['width'] = width
  }
  if (height) {
    dimensions['height'] = height
  }

  // Above-the-fold optimization: use a native <picture> to be able to set fetchpriority, sizes
  // if (!skipGatsbyImage && asset && imageData && aboveFold) {
  //   const sources = imageData.images?.sources || []
  //   const fallback = imageData.images?.fallback
  //   const imgWidth = typeof width !== 'undefined' ? width : (imageData as any).width
  //   const imgHeight = typeof height !== 'undefined' ? height : (imageData as any).height
  //   const computedSizes = sizes || fallback?.sizes || sources?.[0]?.sizes

  //   return (
  //     <picture {...contentfulPreviewProps}>
  //       {sources.map((s: any, i: number) => (
  //         <source key={i} type={s.type} srcSet={s.srcSet} sizes={computedSizes} />
  //       ))}
  //       <img
  //         style={style || imgStyle}
  //         width={imgWidth}
  //         height={imgHeight}
  //         src={fallback?.src || asset?.url}
  //         srcSet={fallback?.srcSet}
  //         sizes={computedSizes}
  //         loading={loading || 'eager'}
  //         fetchPriority="high"
  //         decoding="async"
  //         alt={alt || (asset && asset.title)}
  //         title={title || asset.title}
  //       />
  //     </picture>
  //   )
  // }

  return (!skipGatsbyImage && asset && imageData) ? (
    <GatsbyImage
      loading={loading || (aboveFold ? "eager" : "lazy")}
      image={imageData}
      title={title || asset.title}
      // fetch-priority={aboveFold ? "high" : undefined}
      alt={alt || asset.title}
      imgClassName={aboveFold ? "above-fold" : undefined}
      imgStyle={aboveFold ? { ...style, transition: '', willChange: '', ...dimensions } : { ...style, ...dimensions }}
      {...contentfulPreviewProps}
    />
  ) : asset && asset.url ? (
    <img
      style={style || imgStyle}
      width={imgStyle && imgStyle.width ? imgStyle.width : (width ? width : undefined)}
      height={imgStyle && imgStyle.height ? imgStyle.height : (height ? height : undefined)}
      src={(imageData?.images?.sources && imageData?.images?.sources.length > 0 ? imageData?.images?.sources[0].srcSet.split(' ')[0] : null) || imageData?.images?.fallback?.src || asset?.url}
      srcSet={getSrcSet(imageData)}
      // sizes={sizes}
      loading={loading || (aboveFold ? "eager" : "lazy")}
      fetch-priority={aboveFold ? "high" : undefined}
      alt={alt || (asset && asset.title)}
      title={title || asset.title}
      {...contentfulPreviewProps}
    />
  ) : (
    <></>
  )
}

type KickerProps = {
  icon?: ContentfulAsset
  label: string
  background?: string
}

export function Kicker({ icon, background, ...props }: KickerProps) {
  const theme = useTheme();

  const NotPrintable = useMemo(() => styled(Chip)(notPrintable), []);

  return (
    <NotPrintable
      {...props}
      icon={
        icon ? (
          <ContentfulAssetImage
            asset={icon}
            alt={props.label}
            {...icon}
            imgStyle={{ height: "20px", width: "20px" }}
          />
        ) : undefined
      }
      label={props.label}
      sx={{
        padding: "0.5rem 1rem",
        background: background || theme.palette.primary.light,
        fontWeight: 500,
        color: theme.palette.primary.contrastText,
        fontSize: "1rem",
        lineHeight: "1.5rem",
      }}
      variant="filled"
      color="primary"
    />
  )
}

interface LinkProps {
  to?: string
  href?: string
}

export function Link(
  { to, href, ...props }: LinkProps,
  ref: React.ForwardedRef<unknown>,
) {
  const url = href || to || ""
  if (isAbsoluteURL(url)) {
    return (
      // eslint-disable-next-line jsx-a11y/anchor-has-content
      (<a href={url} target="_blank" {...props} />)
    );
  }
  return <GatsbyLink to={url} {...props} />
}

type NavLinkProps = LinkProps & ButtonProps & {
  contentfulPreviewProps?: any
  children: React.ReactNode
  id: string
}

type NavButtonLinkProps = LinkProps & ButtonProps & {
  contentfulPreviewProps?: any
  children: React.ReactNode
  id: string
}


export function NavLink({ contentfulPreviewProps, id, ...props }: NavLinkProps) {
  const link = props.href || props.to ? React.forwardRef<unknown, LinkProps>((props, ref) =>
    Link(props, ref),
  ) : undefined

  return <MuiButton variant="text" component={link} {...contentfulPreviewProps} {...props} id={id} />
}

export function NavButtonLink({ contentfulPreviewProps, id, ...props }: NavButtonLinkProps) {
  return <MuiButton variant="text" {...contentfulPreviewProps} {...props} id={id} />
}

type ButtonProps = ButtonOwnProps & {
  href?: string
  to?: string
  contentfulPreviewProps?: any
  onClick?: () => void
  'aria-label'?: string
}

export function Button({
  color = "primary",
  variant = "contained",
  href,
  to,
  sx,
  fullWidth,
  children,
  contentfulPreviewProps,
  ...other
}: ButtonProps) {
  let url = href || to
  const isLoginButton = url && url === '/auth/login'
  const hasUrl = !isLoginButton && Boolean(url);

  const isAbsolute = hasUrl && isAbsoluteURL(url)

  let onClick = other.onClick;
  if (isLoginButton) {
    onClick = () => initiateCustomerLogin();
    url = null;
  }

  return isAbsolute || !hasUrl ?
    <MuiButton
      variant={variant}
      color={color}
      href={url}
      rel='noopener noreferrer'
      target="_blank"
      fullWidth={fullWidth}
      sx={sx}
      {...contentfulPreviewProps}
      {...other}
      onClick={onClick}
    >
      {children}
    </MuiButton> :
    <GatsbyLink to={url}>
      <MuiButton
        fullWidth={fullWidth}
        variant={variant}
        color={color}
        sx={sx}
        {...contentfulPreviewProps}
        {...other}
        onClick={onClick}
      >
        {children}
      </MuiButton>
    </GatsbyLink>
}

interface ButtonListProps {
  links: HomepageLink[]
  reversed?: boolean
  buttonVariant?: "text" | "outlined" | "contained"
}
interface ButtonListItemProps {
  link: HomepageLink
  buttonVariant?: "text" | "outlined" | "contained"
}

export function ButtonList({
  links = [],
  reversed = false,
  ...props
}: ButtonListProps) {
  if (!links || links.length === 0) return null
  return (
    <Stack direction={{ xs: "column", sm: "column", md: "row" }} gap={4}>
      {links &&
        links.map((link, i) => (
          <div key={link.id}>
            <ButtonListItem {...props} buttonVariant={i > 0 ? 'outlined' : props.buttonVariant} link={link} />
          </div>
        ))}
    </Stack>
  )
}

export function ButtonListItem({
  link,
  ...props
}: ButtonListItemProps) {
  const inspectorProps = useContentfulInspectorMode();
  const data = useContentfulLiveUpdates({
    ...link,
    sys: { id: link.contentful_id },
  });

  return (
    <Button href={data.href} variant={props.buttonVariant} {...inspectorProps({
      entryId: link.contentful_id,
      fieldId: 'text',
    })}>
      {data.text}
    </Button>
  )
}
